import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Upload, Image as ImageIcon, Trash2, Pencil, Plus, X, Star,
  MapPin, Calendar, Save, Loader2, RefreshCw
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { useAuthStore } from '../store';
import { SEO } from '../components/SEO';
import { toast } from 'sonner';
import { uploadToCloudinary } from './AdminSettingsPage';
import LocalizedField from '../components/admin/LocalizedField';
import TranslateAllButton from '../components/admin/TranslateAllButton';
import AdminHeader from '../components/admin/AdminHeader';
import { useTranslation } from '../i18n/translations.js';

const API = process.env.REACT_APP_BACKEND_URL;

const emptyForm = () => ({
  id: null,
  title: { en: '', nl: '', ro: '', ru: '' },
  description: { en: '', nl: '', ro: '', ru: '' },
  location: { en: '', nl: '', ro: '', ru: '' },
  year: new Date().getFullYear(),
  category: 'residential',
  images: [],
  image_public_ids: [],
  is_featured: false,
  is_published: true,
  slug: '',
});

const getLocationString = (loc) => {
  if (!loc) return '';
  if (typeof loc === 'string') return loc;
  return loc.en || Object.values(loc).find((v) => typeof v === 'string' && v.trim()) || '';
};

const ProjectFormModal = ({ open, onClose, token, initial, onSaved }) => {
  const initialForm = () => {
    if (!initial) return emptyForm();

    // Преобразование старой плоской строки в объект для обратной совместимости
    let locObj = { en: '', nl: '', ro: '', ru: '' };
    if (typeof initial.location === 'string') {
      locObj.en = initial.location;
    } else if (initial.location && typeof initial.location === 'object') {
      locObj = { ...locObj, ...initial.location };
    }

    return {
      id: initial.id,
      title: { en: '', nl: '', ro: '', ru: '', ...(initial.title || {}) },
      description: { en: '', nl: '', ro: '', ru: '', ...(initial.description || {}) },
      location: locObj,
      year: initial.year || new Date().getFullYear(),
      category: initial.category || 'residential',
      images: initial.images || [],
      image_public_ids: initial.image_public_ids || [],
      is_featured: !!initial.is_featured,
      is_published: initial.is_published !== false,
      slug: initial.slug || '',
    };
  };

  const [form, setForm] = useState(initialForm);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const { t } = useTranslation();

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    const valid = files.filter(f => f.type.startsWith('image/'));
    if (valid.length === 0) { toast.error('Please choose image files only'); return; }
    
    setUploading(true);
    try {
      const uploaded = await Promise.all(
        valid.map(async (file) => {
          const up = await uploadToCloudinary(file, token, 'projects', 'image');
          return { url: up.secure_url, public_id: up.public_id };
        }),
      );
      setForm((p) => ({
        ...p,
        images: [...p.images, ...uploaded.map((u) => u.url)],
        image_public_ids: [...p.image_public_ids, ...uploaded.map((u) => u.public_id)],
      }));
      toast.success(`${uploaded.length} image(s) uploaded`);
    } catch (err) {
      toast.error(err.message || 'Image upload failed');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const handleRemoveImage = (idx) => {
    setForm((p) => ({
      ...p,
      images: p.images.filter((_, i) => i !== idx),
      image_public_ids: p.image_public_ids.filter((_, i) => i !== idx),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.en.trim()) {
      toast.error('Title (English) is required'); return;
    }
    if (!form.location.en.trim()) {
      toast.error('Location (English) is required'); return;
    }
    setSaving(true);
    try {
      const url = form.id ? `${API}/api/admin/projects/${form.id}` : `${API}/api/admin/projects`;
      const method = form.id ? 'PUT' : 'POST';
      const payload = {
        title: form.title,
        description: form.description,
        location: form.location,
        year: Number(form.year),
        category: form.category,
        images: form.images,
        image_public_ids: form.image_public_ids,
        is_featured: form.is_featured,
        is_published: form.is_published,
      };
      if (form.slug.trim()) payload.slug = form.slug.trim();
      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Save failed');
      }
      toast.success(form.id ? 'Project updated' : 'Project created');
      onSaved?.();
      onClose?.();
    } catch (err) {
      toast.error(err.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-md flex items-center justify-center p-4" onClick={onClose}>
      <motion.div
        initial={{ opacity: 0, scale: 0.97, y: 12 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        onClick={(e) => e.stopPropagation()}
        className="bg-background w-full max-w-3xl max-h-[92vh] overflow-y-auto rounded-none border border-border shadow-2xl"
        data-testid="project-form-modal"
      >
        <div className="sticky top-0 z-10 bg-background border-b border-border px-8 py-5 flex items-start justify-between">
          <div>
            <p className="text-[10px] tracking-[0.4em] uppercase text-muted-foreground mb-1.5">Portfolio</p>
            <h2 className="text-2xl font-extralight tracking-tight text-gray-900">
              {form.id ? 'Edit Project' : 'New Project'}
            </h2>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-muted" aria-label="Close" data-testid="project-form-close">
            <X className="w-5 h-5" strokeWidth={1.5} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="px-8 py-6 space-y-5">
          <div className="flex justify-end -mb-1">
            <TranslateAllButton
              testid="project-translate-all"
              fields={() => [
                { value: form.title, onChange: (v) => setForm((p) => ({ ...p, title: v })) },
                { value: form.description, onChange: (v) => setForm((p) => ({ ...p, description: v })) },
                { value: form.location, onChange: (v) => setForm((p) => ({ ...p, location: v })) },
              ]}
            />
          </div>
          
          <LocalizedField
            label="Title"
            required
            value={form.title}
            onChange={(next) => setForm((p) => ({ ...p, title: next }))}
            testid="project-title"
            placeholder="e.g. Villa Brussels"
          />

          <LocalizedField
            label="Description"
            multiline rows={4}
            value={form.description}
            onChange={(next) => setForm((p) => ({ ...p, description: next }))}
            testid="project-description"
          />

          <LocalizedField
            label="Location"
            required
            value={form.location}
            onChange={(next) => setForm((p) => ({ ...p, location: next }))}
            testid="project-location"
            placeholder="e.g. Kyiv, Ukraine"
          />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-[10px] tracking-[0.25em] uppercase text-muted-foreground mb-2">Year *</label>
              <input
                type="number" required min="1900" max="2100" value={form.year}
                onChange={(e) => setForm({ ...form, year: e.target.value })}
                className="w-full px-3 py-2 border border-border bg-background text-foreground text-sm focus:outline-none focus:border-black"
                data-testid="project-year"
              />
            </div>
            <div>
              <label className="block text-[10px] tracking-[0.25em] uppercase text-muted-foreground mb-2">Category *</label>
              <select
                value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}
                className="w-full px-3 py-2 border border-border text-sm cursor-pointer font-medium focus:outline-none focus:border-black"
                style={{ color: '#000000', backgroundColor: '#ffffff' }}
                data-testid="project-category"
              >
                <option value="residential" style={{ color: '#000000', backgroundColor: '#ffffff' }}>Residential</option>
                <option value="commercial" style={{ color: '#000000', backgroundColor: '#ffffff' }}>Commercial</option>
                <option value="retail" style={{ color: '#000000', backgroundColor: '#ffffff' }}>Retail</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-[10px] tracking-[0.25em] uppercase text-muted-foreground mb-2">Slug (optional — auto-generated from title)</label>
            <input
              type="text" value={form.slug}
              onChange={(e) => setForm({ ...form, slug: e.target.value })}
              placeholder="villa-brussels"
              className="w-full px-3 py-2 border border-border bg-background text-foreground text-sm font-mono focus:outline-none focus:border-black"
              data-testid="project-slug"
            />
          </div>

          <div>
            <div className="flex items-center justify-between mb-3">
              <label className="text-[10px] tracking-[0.25em] uppercase text-muted-foreground">Images</label>
              <Button type="button" variant="outline" size="sm" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="project-add-images">
                <Upload className="w-3.5 h-3.5 mr-2" strokeWidth={1.5} />
                {uploading ? 'Uploading…' : 'Add Images'}
              </Button>
              <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" onChange={handleImageUpload} />
            </div>
            {form.images.length === 0 ? (
              <div className="border border-dashed border-border p-8 text-center text-sm text-muted-foreground" data-testid="project-images-empty">
                No images yet — add at least one (first image becomes the cover)
              </div>
            ) : (
              <div className="grid grid-cols-3 md:grid-cols-4 gap-3" data-testid="project-images-grid">
                {form.images.map((url, i) => (
                  <div key={`${url}-${i}`} className="relative group aspect-square">
                    <img src={url} alt={`Project ${i + 1}`} className="w-full h-full object-cover border border-border" />
                    {i === 0 && (
                      <span className="absolute top-1 left-1 px-1.5 py-0.5 text-[9px] tracking-wider uppercase bg-foreground text-background font-bold">Cover</span>
                    )}
                    <button
                      type="button"
                      onClick={() => handleRemoveImage(i)}
                      className="absolute top-1 right-1 p-1 bg-background border border-border opacity-0 group-hover:opacity-100 transition-opacity"
                      aria-label="Remove image"
                    >
                      <X className="w-3 h-3" strokeWidth={1.5} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="flex items-center gap-6 pt-2">
            <label className="flex items-center gap-2 cursor-pointer text-sm font-medium">
              <input
                type="checkbox" checked={form.is_published}
                onChange={(e) => setForm({ ...form, is_published: e.target.checked })}
                data-testid="project-published"
                className="accent-black w-4 h-4"
              />
              Published
            </label>
            <label className="flex items-center gap-2 cursor-pointer text-sm font-medium">
              <input
                type="checkbox" checked={form.is_featured}
                onChange={(e) => setForm({ ...form, is_featured: e.target.checked })}
                data-testid="project-featured"
                className="accent-black w-4 h-4"
              />
              Featured
            </label>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t border-border">
            <Button type="button" variant="outline" className="rounded-none h-10 px-4 text-xs" onClick={onClose}>
              {t?.common?.cancel || 'Cancel'}
            </Button>
            <Button type="submit" disabled={saving} className="btn-primary min-w-[140px] rounded-none h-10 text-xs" data-testid="project-save">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" strokeWidth={1.5} /> : <Save className="w-4 h-4 mr-2" strokeWidth={1.5} />}
              {saving ? 'Saving…' : form.id ? 'Save Changes' : 'Create'}
            </Button>
          </div>
        </form>
      </motion.div>
    </div>
  );
};

const AdminProjectsPage = () => {
  const navigate = useNavigate();
  const userFromHook = useAuthStore((s) => s.user);
  const tokenFromHook = useAuthStore((s) => s.token);
  const logout = useAuthStore((s) => s.logout);
  const [hydrated, setHydrated] = useState(false);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const { t } = useTranslation();

  useEffect(() => {
    const id = setTimeout(() => setHydrated(true), 300);
    return () => clearTimeout(id);
  }, []);

  useEffect(() => {
    if (!hydrated) return;
    const u = useAuthStore.getState().user;
    if (!u || u.role !== 'admin') {
      navigate('/admin', { replace: true, state: { from: '/admin/projects' } });
    }
  }, [hydrated, navigate]);

  const fetchProjects = React.useCallback(async () => {
    if (!tokenFromHook) return;
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/admin/projects`, { headers: { Authorization: `Bearer ${tokenFromHook}` } });
      if (res.status === 401 || res.status === 403) { logout(); navigate('/admin'); return; }
      const data = await res.json();
      setProjects(data.projects || []);
    } catch {
      toast.error('Failed to load projects portfolio data');
    } finally {
      setLoading(false);
    }
  }, [tokenFromHook, logout, navigate]);

  useEffect(() => { if (hydrated) fetchProjects(); }, [hydrated, fetchProjects]);

  const handleEdit = (project) => {
    setEditing(project);
    setModalOpen(true);
  };

  const handleNew = () => {
    setEditing(null);
    setModalOpen(true);
  };

  const handleDelete = async (project) => {
    if (!window.confirm(`Delete "${(project.title?.en) || project.slug}"? This cannot be undone.`)) return;
    try {
      const res = await fetch(`${API}/api/admin/projects/${project.id}`, {
        method: 'DELETE', headers: { Authorization: `Bearer ${tokenFromHook}` },
      });
      if (!res.ok) throw new Error();
      toast.success('Project deleted');
      fetchProjects();
    } catch {
      toast.error('Failed to delete project');
    }
  };

  if (!hydrated) return <div className="min-h-screen flex items-center justify-center text-xs text-gray-400 bg-background">Loading…</div>;
  if (!userFromHook || userFromHook.role !== 'admin') return null;

  return (
    <div className="min-h-screen bg-background text-left font-light" data-testid="admin-projects-page">
      <SEO title="Projects · Admin" description="Manage portfolio projects" path="admin/projects" />

      <AdminHeader active="projects" />

      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
          <div>
            <p className="text-caption text-muted-foreground mb-3 text-xs tracking-wider uppercase text-gray-400 font-semibold">CMS · Portfolio</p>
            <h1 className="text-4xl md:text-5xl font-extralight tracking-tight text-gray-900">Projects</h1>
            <p className="text-muted-foreground mt-3 max-w-xl text-sm leading-relaxed text-gray-500">
              Manage the brand lookbook and bespoke architecture portfolio displayed publicly on <code className="bg-muted px-1.5 py-0.5">/projects</code>.
            </p>
          </div>
          
          <div className="flex items-center gap-3">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={fetchProjects} 
              disabled={loading} 
              className="h-10 bg-white text-gray-900 border-gray-200 text-xs rounded-none px-4 shadow-sm hover:bg-neutral-50 flex items-center transition-colors font-medium"
              style={{ height: '40px' }}
            >
              <RefreshCw className={`w-3.5 h-3.5 mr-2 ${loading ? 'animate-spin' : ''}`} strokeWidth={1.5} />Refresh
            </Button>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={handleNew} 
              className="h-10 bg-white text-gray-900 border-gray-200 text-xs rounded-none px-4 shadow-sm hover:bg-neutral-50 flex items-center transition-colors font-medium"
              style={{ height: '40px' }}
              data-testid="admin-new-project-btn"
            >
              <Plus className="w-3.5 h-3.5 mr-2" strokeWidth={1.5} />New Project
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="py-24 text-center text-muted-foreground text-xs">Loading…</div>
        ) : projects.length === 0 ? (
          <div className="py-24 text-center text-muted-foreground border border-border bg-white rounded-none shadow-sm text-xs" data-testid="admin-projects-empty">
            <ImageIcon className="w-10 h-10 mx-auto mb-4 text-muted-foreground" strokeWidth={1} />
            <p className="text-muted-foreground mb-6">No projects found in database log tracks.</p>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={handleNew} 
              className="h-10 bg-white text-gray-900 border-gray-200 text-xs rounded-none px-4 shadow-sm hover:bg-neutral-50 flex items-center transition-colors font-medium mx-auto"
              style={{ height: '40px' }}
            >
              <Plus className="w-3.5 h-3.5 mr-2" strokeWidth={1.5} />Create your first project
            </Button>
          </div>
        ) : (
          <div className="border border-border divide-y divide-border bg-white rounded shadow-sm" data-testid="admin-projects-list">
            {projects.map((p) => (
              <div key={p.id} className="flex items-center gap-5 p-4" data-testid={`admin-project-${p.id}`}>
                <div className="w-20 h-16 bg-muted overflow-hidden flex-shrink-0 border border-gray-100 rounded">
                  {p.images?.[0] ? (
                    <img src={p.images[0]} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-muted-foreground">
                      <ImageIcon className="w-5 h-5" strokeWidth={1.2} />
                    </div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-gray-900 truncate text-sm">{p.title?.en || p.slug}</p>
                  <div className="flex items-center gap-3 mt-1 text-[11px] text-muted-foreground font-normal">
                    <span className="flex items-center gap-1 text-gray-500">
                      <MapPin className="w-3 h-3 text-gray-400" strokeWidth={1.5} />
                      {getLocationString(p.location)}
                    </span>
                    <span className="flex items-center gap-1 text-gray-500">
                      <Calendar className="w-3 h-3 text-gray-400" strokeWidth={1.5} />
                      {p.year}
                    </span>
                    <span className="capitalize px-1.5 py-0.5 bg-gray-100 rounded text-[10px] text-gray-600 font-medium">{p.category}</span>
                    {p.is_featured && <Star className="w-3 h-3 fill-amber-500 text-amber-500" strokeWidth={0} />}
                    {!p.is_published && <span className="px-1.5 py-0.5 bg-neutral-900 text-white text-[9px] tracking-wider uppercase font-bold rounded-sm">Draft</span>}
                  </div>
                </div>
                
                <div className="flex items-center gap-2 shrink-0">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => handleEdit(p)} 
                    className="rounded-none text-xs h-9 bg-white text-gray-900 border-gray-200 transition-colors p-0 w-11"
                    data-testid={`admin-project-edit-${p.id}`}
                  >
                    <Pencil className="w-3.5 h-3.5" strokeWidth={1.5} />
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => handleDelete(p)} 
                    className="rounded-none text-xs h-9 bg-white text-destructive border-gray-200 transition-colors p-0 w-11 hover:bg-rose-50" 
                    data-testid={`admin-project-delete-${p.id}`}
                  >
                    <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <AnimatePresence>
        {modalOpen && (
          <ProjectFormModal
            key={editing?.id || 'new'}
            open={modalOpen}
            onClose={() => setModalOpen(false)}
            token={tokenFromHook}
            initial={editing}
            onSaved={fetchProjects}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default AdminProjectsPage;
